import {createAsyncThunk, createSlice} from "@reduxjs/toolkit"
import {asyncCreator, catchError} from "./sliceHelper"
import {authApi, LoginForm} from "../api/authApi"
import {BaseRequestParams} from "../api/API"
import {appApi, StatisticsResponse} from "../api/appApi"
import {userRoleApi} from "../api/userRolesApi"
import {psLeakDeviceLocalLogApi} from "../api/psLeakDeviceLocalLogApi"
import {limitOptionsSelect} from "../helps/optionsSelect"
import {notificationApi, NotificationsResponse} from "../api/notificationApi"
import {NotificationResponse, PSLeakDeviceLocalLogsResponse, UserResponse, UserRolesResponse} from "../models/response"
import {psLeakDeviceApi} from "../api/psLeakDeviceApi"
import {ProblemsResponse} from "../models/response/ProblemsResponse"

export type DashboardFilter = {
    limit: number
    offset: number
    idCompany?: number
    idObjectGroup?: number
    idObject?: number
    idDevice?: number
    dateFrom?: string | null | undefined
    dateTo?: string | null | undefined
    search?: string
}

type InitialStateType = {
    statistics: StatisticsResponse | null,
    notifications: NotificationsResponse | null,
    userRoles: null | UserRolesResponse
    profile: UserResponse | null,
    idUser: number | null,
    status: "loading" | "success" | "reject" | "idle"
    message: {
        message: string,
        type: "success" | "error" | "warning" | "info" | ""
        sound: boolean
    },
    dashboardLogs: null | PSLeakDeviceLocalLogsResponse
    dashboardProblems: null | Array<ProblemsResponse>
    filter: DashboardFilter
}

const initialState: InitialStateType = {
    statistics: null,
    notifications: null,
    userRoles: null,
    profile: null,
    idUser: null,
    status: "idle",
    message: {
        message: "",
        type: "",
        sound: false,
    },
    dashboardLogs: null,
    dashboardProblems: null,
    filter: {
        limit: limitOptionsSelect[0].value,
        offset: 0,
    },
}

export const appReducer = createSlice({
    name: "appReducer",
    initialState,
    reducers: {
        setProfile(state, {payload})
        {
            state.profile = payload
        },
        setIdUser(state, {payload})
        {
            state.idUser = payload
        },
        setStatus(state, {payload})
        {
            state.status = payload
        },
        setMessage(state, {payload})
        {
            state.message = payload
        },
        setStatistics(state, {payload})
        {
            state.statistics = payload
        },
        setNotViewedNotificationsCount(state, {payload})
        {
            state.notifications = {
                data: [...state.notifications?.data || []],
                total: payload.total,
            }
        },
        setNotifications(state, {payload})
        {
            state.notifications = payload
        },
        setMoreNotifications(state, {payload})
        {
            const newList: NotificationResponse[] = [...state.notifications?.data?.sort((a, b) => b.id - a.id) || []]
            const existIds = newList.map(i => i.id)

            for (const i of payload.data as NotificationResponse[])
            {
                if (!existIds.includes(i.id))
                    newList.push(i)
            }

            state.notifications = {
                data: newList,
                total: payload.total,
            }
        },
        removeViewedNotifications(state, {payload})
        {
            state.notifications = {
                data: payload === 0 ? [] : [...state.notifications?.data?.filter(i => i.id !== payload) || []],
                total: payload === 0 ? 0 : state.notifications?.total || 1 - 1,
            }
        },
        setUserRoles(state, {payload})
        {
            state.userRoles = payload
        },
        setDashboardLogs(state, {payload})
        {
            state.dashboardLogs = payload
        },
        setDashboardFilter(state, {payload})
        {
            state.filter = payload
        },
        setDashboardProblems(state, {payload})
        {
            state.dashboardProblems = payload
        },
    },
})

export const {
    setStatus, setMessage, setProfile, setIdUser, setStatistics, setUserRoles, setDashboardLogs, setDashboardFilter, setNotViewedNotificationsCount, setNotifications, setMoreNotifications,
    setDashboardProblems, removeViewedNotifications
} = appReducer.actions

export default appReducer.reducer

export const login = createAsyncThunk(
    "appReducer/login",
    async (params: LoginForm, {dispatch}) =>
    {
        dispatch(setStatus("loading"))
        try
        {
            const res = await authApi.login(params)
            if (res.status === 0)
            {
                dispatch(setProfile(res.data.user))
                dispatch(setIdUser(res.data.user.id))
                dispatch(setStatus("success"))
            }
            else
            {
                dispatch(setMessage({type: "error", message: res.message}))
                dispatch(setStatus("reject"))
            }
            return res
        } catch (e: any)
        {
            catchError(e, dispatch)
        }
    },
)

export const logout = createAsyncThunk(
    "appReducer/logout",
    async (params: BaseRequestParams, {dispatch}) =>
    {
        dispatch(setStatus("loading"))
        try
        {
            const res = await authApi.logout(params)
            if (res.status === 0)
            {
                dispatch(setProfile(null))
                dispatch(setIdUser(null))
                dispatch(setStatus("success"))
            }
            else
            {
                dispatch(setMessage({type: "error", message: res.message}))
                dispatch(setStatus("reject"))
            }
            return res
        } catch (e: any)
        {
            catchError(e, dispatch)
        }
    },
)

export const checkAuth = createAsyncThunk(
    "appReducer/checkAuth",
    async (params: BaseRequestParams, {dispatch}) =>
    {
        dispatch(setStatus("loading"))
        try
        {
            const res = await authApi.checkAuth(params)
            if (res.status === 0)
            {
                dispatch(setProfile(res.data))
                dispatch(setIdUser(res.data.id))
                dispatch(setStatus("success"))
            }
            else
            {
                dispatch(setStatus("reject"))
            }
            return res
        } catch (e: any)
        {
            catchError(e, dispatch)
        }
    },
)

export const getStatistics = asyncCreator
("appReducer/getStatistics", appApi.getStatistics, setStatistics)

export const getNotViewedNotificationsCount = asyncCreator
("appReducer/getNotViewedNotificationsCount", notificationApi.getNotifications, setNotViewedNotificationsCount)

export const getNotifications = asyncCreator
("appReducer/getNotifications", notificationApi.getNotifications, setNotifications)

export const getMoreNotifications = asyncCreator
("appReducer/getMoreNotifications", notificationApi.getNotifications, setMoreNotifications)

export const setViewedNotifications = asyncCreator
("appReducer/setViewedNotifications", notificationApi.setViewed, removeViewedNotifications)

export const getUserRoles = asyncCreator
("appReducer/getUserRoles", userRoleApi.getUserRoles, setUserRoles)

export const getDashboardLogs = asyncCreator
("appReducer/getDashboardLogs", psLeakDeviceLocalLogApi.getLogs, setDashboardLogs)

export const getDashboardProblems = asyncCreator
("appReducer/getDashboardProblems", psLeakDeviceApi.getProblems, setDashboardProblems)